import { type Reducer, useEffect, useReducer } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { useMessageStore } from '@/stores/message';
import { isMedia } from '@/utils/check-messages-status';

export default function useMediaMessages(
  messageId?: Nullable<number>,
  filterType: MediaMessageFilterReducerActionType = 'all',
) {
  const messages = useMessageStore(useShallow((s) => Array.from(s.messages.values())));
  const [state, dispatch] = useReducer<MediaMessageFilterReducer>(
    mediaMessageFilterReducer,
    createInitialState(filterType, messages),
  );
  const messageIndex = state.messages.findIndex((m) => m.ID === messageId);

  useEffect(() => {
    dispatch({
      type: filterType,
      params: { messages },
    });
  }, [messages]);

  return {
    messages: state.messages,
    messageIndex,
    message: messageIndex > -1 ? state.messages.at(messageIndex) : undefined,
    filteredBy: state.filteredBy,
    setFilter: (type: MediaMessageFilterReducerActionType) => dispatch({ type, params: { messages } }),
  };
}

export type MediaMessageFilterReducerActionType = 'all' | 'images' | 'videos' | 'files';

type MediaMessageFilterReducerAction = {
  type: MediaMessageFilterReducerActionType;
  params: {
    messages: ConversationMessageTypeUnion[];
  };
};

type MediaMessageFilterReducerState = {
  filteredBy: MediaMessageFilterReducerActionType;
  messages: ConversationMessageTypeUnion[];
};

type MediaMessageFilterReducer = Reducer<MediaMessageFilterReducerState, MediaMessageFilterReducerAction>;

function createInitialState(
  filterType: MediaMessageFilterReducerActionType,
  messages: ConversationMessageTypeUnion[],
): MediaMessageFilterReducerState {
  return mediaMessageFilterReducer(
    {
      filteredBy: filterType,
      messages: [],
    },
    {
      type: filterType,
      params: { messages },
    },
  );
}

function mediaMessageFilterReducer(
  state: MediaMessageFilterReducerState,
  action: MediaMessageFilterReducerAction,
): MediaMessageFilterReducerState {
  const messages = action.params.messages;
  const newState = { ...state, filteredBy: action.type };

  switch (action.type) {
    case 'all':
      newState.messages = messages.filter(isMedia);
      break;
    case 'images':
      newState.messages = messages.filter((m) => m.MsgType === ConversationMessageContentType.Image);
      break;
    case 'videos':
      newState.messages = messages.filter((m) => m.MsgType === ConversationMessageContentType.Video);
      break;
    case 'files':
      newState.messages = messages.filter((m) => m.MsgType === ConversationMessageContentType.File);
      break;
    default:
      return state;
  }

  return newState;
}
